import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faLinkedinIn,
  faTwitter,
  faGithub,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faExternalLinkAlt,
  faPhoneAlt,
} from "@fortawesome/free-solid-svg-icons";
import { baseUrl } from "../../config";

const navLinks = [
  { name: "Services", url: "/services" },
  { name: "Tools", url: "/tools" },
  { name: "Blogs", url: "/blogs" },
  { name: "Contact", url: "/contact-us" },
];

const resourceLinks = [
  { name: "Privacy Policy", url: "/privacy-policy" },
  { name: "Sitemap", url: "/sitemap" },
  { name: "Templates", url: "/templates" },
];

const Footer = () => {
  const siteNavigationStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: navLinks.map((l, i) => ({
      "@type": "SiteNavigationElement",
      position: i + 1,
      name: l.name,
      url: baseUrl + l.url,
    })),
  };

  const contactPointStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "GeniusNexa",
    url: baseUrl,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "support@geniusnexa.com",
        telephone: "+91 9944544603",
        areaServed: "Global",
        availableLanguage: ["en"],
      },
    ],
  };

  const socialLinks = [
    {
      icon: faFacebookF,
      url: "https://www.facebook.com/yourprofile",
      label: "Facebook",
    },
    {
      icon: faLinkedinIn,
      url: "https://www.linkedin.com/company/geniusnexa",
      label: "LinkedIn",
    },
    {
      icon: faTwitter,
      url: "https://twitter.com/geniusnexa",
      label: "Twitter",
    },
    { icon: faGithub, url: "https://github.com/geniusnexa", label: "GitHub" },
  ];

  return (
    <footer
      className="gn-footer gn-footer--enhanced relative"
      aria-label="Footer"
    >
      <div className="footer-overlay-gradient" aria-hidden="true" />
      <div className="footer-overlay-noise" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4">
        {/* Top CTA Strip */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] rounded-2xl px-8 py-8 shadow-lg mb-20 gap-6">
          <div>
            <h3 className="text-2xl font-bold tracking-tight mb-2">
              Ready to accelerate innovation?
            </h3>
            <p className="text-sm md:text-base font-medium opacity-80">
              Book a consultation to explore how AI & cloud can unlock growth.
            </p>
          </div>
          <a
            href="/contact-us"
            className="inline-flex items-center justify-center bg-[#0b1220] text-[#E5C558] hover:bg-black/90 px-6 py-4 rounded-full font-semibold text-sm shadow-md transition-colors"
          >
            Schedule a Consultation
            <svg
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>

        <div className="grid gap-14 lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2">
          {/* Brand */}
          <div className="space-y-6 lg:col-span-1">
            <Link to="/" className="inline-flex items-center">
              <img
                src={baseUrl + "/images/logo.svg"}
                alt="GeniusNexa logo"
                className="h-14 w-auto drop-shadow"
                loading="lazy"
              />
            </Link>
            <p className="text-sm leading-relaxed text-gray-400 max-w-xs">
              Empowering enterprises with AI-driven transformation, secure cloud
              architectures and intelligent data products.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  aria-label={s.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-[#E5C558] hover:text-[#0b1220] transition-colors"
                >
                  <FontAwesomeIcon icon={s.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <nav aria-label="Navigation" className="space-y-6">
            <h3 className="footer-heading">Navigation</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.url}>
                  <Link to={link.url} className="footer-link">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Solutions (derived from nav + resources) */}
          <nav aria-label="Solutions" className="space-y-6">
            <h3 className="footer-heading">Resources</h3>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.url}>
                  <Link to={link.url} className="footer-link">
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={baseUrl + "/sitemap.xml"}
                  className="footer-link flex items-center"
                >
                  XML Sitemap{" "}
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    className="ml-2 text-xs"
                  />
                </a>
              </li>
            </ul>
          </nav>

          {/* Contact */}
          <div aria-label="Contact" className="space-y-6">
            <h3 className="footer-heading">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center text-gray-400">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="text-[#E5C558] mr-3"
                />{" "}
                support@geniusnexa.com
              </li>
              <li className="flex items-center text-gray-400">
                <FontAwesomeIcon
                  icon={faPhoneAlt}
                  className="text-[#E5C558] mr-3"
                />{" "}
                +91 9944544603
              </li>
            </ul>
            <p className="text-[11px] text-gray-500 leading-relaxed max-w-xs">
              We respond within one business day. Global delivery & support.
            </p>
          </div>

          {/* Newsletter */}
          <div aria-label="Newsletter" className="space-y-6 lg:col-span-1">
            <h3 className="footer-heading">Stay Updated</h3>
            <p className="text-sm text-gray-400">
              Insights on AI, product innovation & cloud maturity.
            </p>
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <label className="sr-only" htmlFor="footer-email">
                Email
              </label>
              <input
                id="footer-email"
                type="email"
                required
                placeholder="you@example.com"
                className="px-4 py-3 rounded-lg bg-white/10 backdrop-blur border border-white/20 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E5C558]"
              />
              <button
                type="submit"
                className="btn btn-primary rounded-xl w-full shadow-md hover:shadow-[#E5C558]/30"
              >
                Subscribe
              </button>
            </form>
            <p className="text-[11px] text-gray-500">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="mt-20 border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center text-xs text-gray-500">
          <p>
            &copy; {new Date().getFullYear()} GeniusNexa. All rights reserved.
          </p>
          <p className="mt-4 sm:mt-0">Empowering intelligent transformation.</p>
        </div>
      </div>
      <script type="application/ld+json">
        {JSON.stringify(siteNavigationStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(contactPointStructuredData)}
      </script>
    </footer>
  );
};

export default Footer;
